// A lot's internal notes offline (BL-2447 AC 5, ADR 066 — Offline Mode § 4.4): adding, editing and
// deleting a note each queue one entry and change the device's copy of the lot, so the notes panel
// shows it at once. Each answer is the notes route's own DTO. Only lot notes are on the device.
import { InternalNoteBodyTextSchema } from '@auctogon/core-admin/schemas/internal-note';
import type { InternalNoteDto } from '@auctogon/core-admin/schemas/responses/internal-note';
import type { OfflineDeltaLotDto } from '@auctogon/core-admin/schemas/responses/offline-sync';
import {
  buildQueuedEntry,
  isRecord,
  lotIdOfGroupKey,
  type QueuedEntryInput,
} from '@auctogon/admin-isomorphic/isomorphic/offline/change-queue-entry';
import { offlineNote, withNoteEdited } from '@auctogon/admin-isomorphic/isomorphic/offline/offline-notes';

import type { OfflineScope, OfflineStore } from '../../gateways/native-device';
import type { OfflineRequest } from './create-offline-transport';
import { sessionAuthor } from './offline-write-handlers';
import { NOT_FOUND } from './offline-answers';
import { commitLocalWrite, isQueuedNewLot, labelOf, pendingCreateOf, unqueueable, withQueue, type WriteContext } from './offline-write-support';

const newId = () => globalThis.crypto.randomUUID();

/** The note text as the route validates it, or the route's own refusal. */
function noteText(body: unknown): { ok: true; text: string } | { ok: false; error: string } {
  const parsed = InternalNoteBodyTextSchema.safeParse(isRecord(body) ? body.body : undefined);
  if (parsed.success) return { ok: true, text: parsed.data };
  return { ok: false, error: parsed.error.issues[0]?.message ?? 'Invalid note' };
}

/**
 * The lot each note the notes panel listed hangs off, per scope: the panel always lists a lot's
 * notes before an edit or delete, so the handler finds the lot without decoding every cached lot.
 */
const noteOwners = new Map<string, string>();
const ownerKey = (scope: OfflineScope, noteId: string) => `${scope.tenantId}:${scope.userId}:${noteId}`;

/**
 * Test seam: forget every remembered note owner.
 *
 * @returns nothing.
 * @sideEffect Clears the in-memory note owner index.
 */
export function __resetOfflineNoteOwnersForTest(): void {
  noteOwners.clear();
}

/**
 * Remember which lot a listed note hangs off.
 *
 * @param input.scope - the scope the notes were read in.
 * @param input.lotId - their lot.
 * @param input.notes - the notes.
 * @sideEffect Updates the in-memory note owner index.
 */
export function rememberNoteOwners(input: { scope: OfflineScope; lotId: string; notes: readonly { id: string }[] }): void {
  for (const note of input.notes) noteOwners.set(ownerKey(input.scope, note.id), input.lotId);
}

/** The lot a note hangs off: named by its queued create, or the panel's last read, else found. */
async function lotOfNote(store: OfflineStore, noteId: string): Promise<OfflineDeltaLotDto | null> {
  const entry = await store.outbox.get(noteId);
  const knownLotId = (entry?.op === 'note.create' ? lotIdOfGroupKey(entry.groupKey) : null) ?? noteOwners.get(ownerKey(store.scope, noteId));
  if (knownLotId) {
    const lot = await store.records.get({ collection: 'lots', key: knownLotId });
    if (lot?.notes.some((note) => note.id === noteId)) return lot;
  }
  // Not listed this session (a stale index, or a call from outside the panel): look through every lot.
  const lots = await store.records.list({ collection: 'lots' });
  return lots.find((lot) => lot.notes.some((note) => note.id === noteId)) ?? null;
}

/**
 * What an edit or delete of a note waits on: the note's own create while it is queued (plan Q6),
 * else nothing. A note typed on a lot's create sheet rides that create and has no id of its own
 * until it syncs, so it can't be changed before then.
 */
async function noteDependency(
  store: OfflineStore,
  input: { noteId: string; lotIsNew: boolean },
): Promise<{ dependsOn: string | null } | null> {
  const entry = await store.outbox.get(input.noteId);
  if (entry?.op === 'note.create') return { dependsOn: entry.id };
  return input.lotIsNew ? null : { dependsOn: null };
}

async function queueNoteCreate({ req, store, queue }: WriteContext) {
  const body = req.body;
  // Only a lot's notes are on the device.
  if (!isRecord(body) || body.entityType !== 'lot' || typeof body.entityId !== 'string') return unqueueable();
  const text = noteText(body);
  if (!text.ok) return { kind: 'error' as const, status: 400, error: text.error };
  const lotId = body.entityId;
  const lot = await store.records.get({ collection: 'lots', key: lotId });
  if (!lot) return NOT_FOUND;
  const author = await sessionAuthor(store);
  if (!author) return unqueueable();
  const id = newId();
  const note = offlineNote({ id, lotId, body: text.text, author, at: new Date().toISOString() });
  const createId = await pendingCreateOf(store, lotId);
  const lotIsNew = await isQueuedNewLot(store, lotId);
  const entry = buildQueuedEntry({
    id,
    op: 'note.create',
    // The entry id is the note's `clientOpId` (BL-2337), so a retried create answers with one note.
    call: { params: {}, query: null, body: { entityType: 'lot', entityId: lotId, body: text.text, clientOpId: id } },
    label: labelOf(lot, lotIsNew),
    lotId,
    dependsOn: createId,
  });
  await commitLocalWrite({ store, queue, entries: [entry], lot: { key: lotId, value: { ...lot, notes: [note, ...lot.notes] }, parentKey: lot.detail.auctionId } });
  rememberNoteOwners({ scope: store.scope, lotId, notes: [note] });
  return { kind: 'ok' as const, data: note };
}

/** Queue an edit or delete of one note, and rewrite the lot's cached notes. */
async function queueNoteChange(input: {
  ctx: WriteContext;
  op: 'note.update' | 'note.delete';
  text: string | null;
}): Promise<{ note: InternalNoteDto | null } | typeof NOT_FOUND> {
  const { ctx: { req, store, queue }, op, text } = input;
  const noteId = req.params.noteId;
  const lot = noteId ? await lotOfNote(store, noteId) : null;
  const current = lot?.notes.find((note) => note.id === noteId);
  if (!noteId || !lot || !current) return NOT_FOUND;
  // Author only, as the route decides (ADR 052 — Internal Notes § 4); the panel offers Edit on the same flag.
  if (op === 'note.update' && !current.viewerCanEdit) return unqueueable();
  const lotId = lot.detail.id;
  const lotIsNew = await isQueuedNewLot(store, lotId);
  const dependency = await noteDependency(store, { noteId, lotIsNew });
  if (!dependency) return unqueueable();
  const edited = text == null ? null : withNoteEdited({ notes: lot.notes, noteId, body: text, at: new Date().toISOString() });
  const notes = edited ? edited.notes : lot.notes.filter((note) => note.id !== noteId);
  const entry: QueuedEntryInput = buildQueuedEntry({
    id: newId(),
    op,
    call: { params: { noteId }, query: null, body: text == null ? null : { body: text } },
    label: labelOf(lot, lotIsNew),
    lotId,
    dependsOn: dependency.dependsOn,
  });
  await commitLocalWrite({ store, queue, entries: [entry], lot: { key: lotId, value: { ...lot, notes }, parentKey: lot.detail.auctionId } });
  return { note: edited?.note ?? null };
}

async function queueNoteUpdate(ctx: WriteContext) {
  const text = noteText(ctx.req.body);
  if (!text.ok) return { kind: 'error' as const, status: 400, error: text.error };
  const result = await queueNoteChange({ ctx, op: 'note.update', text: text.text });
  if ('kind' in result) return result;
  return result.note ? { kind: 'ok' as const, data: result.note } : NOT_FOUND;
}

async function queueNoteDelete(ctx: WriteContext) {
  const result = await queueNoteChange({ ctx, op: 'note.delete', text: null });
  if ('kind' in result) return result;
  return { kind: 'ok' as const, data: null };
}

/** The note writes, keyed `"<registry key>:<VERB>"`; typed by `OfflineHandlers` where composed. */
export const noteWriteHandlers = {
  'internal_notes.index:POST': (req: OfflineRequest) => withQueue(req, queueNoteCreate),
  'internal_notes.detail:PATCH': (req: OfflineRequest) => withQueue(req, queueNoteUpdate),
  'internal_notes.detail:DELETE': (req: OfflineRequest) => withQueue(req, queueNoteDelete),
};
